import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import is from '@sindresorhus/is';
import { useContext } from 'react';
import { useParams } from 'react-router';

import { RelevantAvailableLearningOpportunitiesSection } from './RelevantAvailableLearningOpportunitiesSection';
import { ApplicationContext } from '../../../../shared/source/context/context';
import { CollapsibleWithHeadline } from '../../design/components/collapsible/CollapsibleWithHeadline';
import { SkillsRow } from '../../design/components/SkillsRow';
import { Headline, SubTitle, Subtitle2 } from '../../design/components/Typography';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { route } from '../pages/job-list/constants';

import type { UserJobRelevantSkills } from '../../../../shared/source/data-contract/user-job';
import type { FunctionComponent } from 'react';

type SkillsMatchingCardProps = {
    readonly ariaLabel?: string;
    readonly description: string;
    readonly headline: string;
    readonly matchingTitle: string;
    readonly mismatchingTitle: string;
    readonly recommendationData: UserJobRelevantSkills;
    readonly showRelevantLearningOpportunities?: boolean;
};

export const SkillsMatchingCard: FunctionComponent<SkillsMatchingCardProps> = ({
    ariaLabel,
    description,
    headline,
    matchingTitle,
    mismatchingTitle,
    recommendationData,
    showRelevantLearningOpportunities = true,
}) => {
    const hasMismatchingSkills = is.nonEmptyArray(recommendationData.missingSkills);
    const hasMatchingSkills = is.nonEmptyArray(recommendationData.matchingSkills);
    const { applicationState } = useContext(ApplicationContext);
    const { language } = applicationState;
    const { sendTrackingEvent } = useApplicationFetchers();
    const { matchingScore } = recommendationData;
    const jobId = useParams()[route.params.jobId] ?? '';

    const sendRecommendationUsedTrackingEvent = (): void => {
        sendTrackingEvent({
            name: 'recommendation-used',
            payload: {
                entity: 'learning-opportunity',
                entryPoint: 'job-details',
                matchingScore,
                recommendationSource: 'system',
            },
        });
    };

    return (
        <CollapsibleWithHeadline ariaLabel={ariaLabel} headline={headline} isInitiallyExpanded={true}>
            <Grid container={true} flexDirection="column" gap={4}>
                {!is.nullOrUndefined(matchingScore) && (
                    <Grid color="jobposting.main">
                        <Box alignItems="center" display="flex" gap={2}>
                            <Headline variant="h4">
                                {matchingScore.toLocaleString(language.code, { style: 'percent' })}
                            </Headline>
                            <SubTitle color="jobposting.main">{description}</SubTitle>
                        </Box>
                    </Grid>
                )}
                {hasMatchingSkills && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Subtitle2>{matchingTitle}</Subtitle2>
                        <SkillsRow max={3} skills={recommendationData.matchingSkills} />
                    </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Subtitle2>{mismatchingTitle}</Subtitle2>
                    <SkillsRow chipsOutlined={true} max={3} skills={recommendationData.missingSkills} />
                </Box>
                {hasMismatchingSkills && showRelevantLearningOpportunities && (
                    <RelevantAvailableLearningOpportunitiesSection
                        jobId={jobId}
                        missingSkills={recommendationData.missingSkills}
                        onLearningOpportunityClick={sendRecommendationUsedTrackingEvent}
                    />
                )}
            </Grid>
        </CollapsibleWithHeadline>
    );
};
